import React from 'react';
import styled, { css } from 'styled-components';

type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'minimal';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}

interface StyledButtonProps {
  variant: ButtonVariant;
  size: ButtonSize;
  disabled: boolean;
}

const getVariantStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return css`
        background: linear-gradient(to bottom, #AFCF88, #8BA96E);
      `;
    case 'secondary':
      return css`
        background: linear-gradient(to bottom, #6A89A6, #4F728F);
      `;
    case 'success':
      return css`
        background: linear-gradient(to bottom, #5DA860, #4A844F);
      `;
    case 'danger':
      return css`
        background: linear-gradient(to bottom, #D9534F, #B2403F);
      `;
    case 'minimal':
      return css`
        background: transparent;
        color: #8BA96E;
        border: 2px solid #8BA96E;
      `;
    default:
      return css`
        background: linear-gradient(to bottom, #AFCF88, #8BA96E);
      `;
  }
};

const StyledButton = styled.button<StyledButtonProps>`
  color: white;
  font-weight: bold;
  padding: ${({ size }: StyledButtonProps) => {
    switch (size) {
      case 'small':
        return '8px 16px';
      case 'large':
        return '16px 24px';
      default:
        return '12px 20px';
    }
  }};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  text-decoration: none;
  font-size: ${({ size }: StyledButtonProps) => {
    switch (size) {
      case 'small':
        return '14px';
      case 'large':
        return '18px';
      default:
        return '16px';
    }
  }};

  ${({ variant }: StyledButtonProps) => getVariantStyles(variant)}

  ${({ disabled }: StyledButtonProps) =>
    disabled &&
    css`
      background: #E0E0E0;
      color: #A0A0A0;
      cursor: not-allowed;
      box-shadow: none;
    `}

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  disabled = false,
  onClick,
  children,
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </StyledButton>
  );
};

export default Button; 